import React, { useEffect, useRef } from 'react';

const STATE_COLORS = {
  BOOTING: '#B9F3FF',
  ARMED: '#00D7FF',
  LISTENING: '#19F2D6',
  THINKING: '#FFB020',
  SPEAKING: '#00D7FF',
  STANDBY: '#7C8AA6',
  ERROR: '#FF3B3B'
};

export default function ArcReactor({ state = 'ARMED' }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const stateRef = useRef(state);

  useEffect(() => { stateRef.current = state; }, [state]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const size = 240;
    canvas.width = size;
    canvas.height = size;
    const cx = size / 2;
    const cy = size / 2;
    let angle = 0;
    let pulsePhase = 0;
    
    function draw() {
      const currentState = stateRef.current;
      const color = STATE_COLORS[currentState] || STATE_COLORS.ARMED;
      const isActive = currentState === 'THINKING' || currentState === 'LISTENING' || currentState === 'SPEAKING';
      const speed = isActive ? 0.03 : 0.01;
      const pulseSpeed = currentState === 'THINKING' ? 0.08 : currentState === 'SPEAKING' ? 0.06 : 0.02;
      
      ctx.clearRect(0, 0, size, size);
      angle += speed;
      pulsePhase += pulseSpeed;
      
      const pulse = 0.7 + 0.3 * Math.sin(pulsePhase);
      
      // Background glow
      const bgGrad = ctx.createRadialGradient(cx, cy, 10, cx, cy, 110);
      bgGrad.addColorStop(0, `${color}${Math.round(pulse * 25).toString(16).padStart(2, '0')}`);
      bgGrad.addColorStop(0.5, `${color}08`);
      bgGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, size, size);
      
      // Outer ring with segments
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle);
      for (let i = 0; i < 12; i++) {
        const startA = (i * 30 + 3) * Math.PI / 180;
        const endA = ((i + 1) * 30 - 3) * Math.PI / 180;
        ctx.beginPath();
        ctx.arc(0, 0, 100, startA, endA);
        ctx.strokeStyle = `${color}${i % 3 === 0 ? '60' : '30'}`;
        ctx.lineWidth = i % 3 === 0 ? 2 : 1;
        ctx.stroke();
      }
      ctx.restore();
      
      // Mid ring (reverse rotation)
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(-angle * 1.5);
      for (let i = 0; i < 8; i++) {
        const startA = (i * 45 + 4) * Math.PI / 180;
        const endA = ((i + 1) * 45 - 4) * Math.PI / 180;
        ctx.beginPath();
        ctx.arc(0, 0, 78, startA, endA);
        ctx.strokeStyle = `${color}${Math.round(pulse * 60).toString(16).padStart(2, '0')}`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
      ctx.restore();
      
      // Inner ring
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle * 2);
      ctx.beginPath();
      ctx.arc(0, 0, 55, 0, Math.PI * 2);
      ctx.strokeStyle = `${color}40`;
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Tick marks
      for (let i = 0; i < 24; i++) {
        const a = (i * 15) * Math.PI / 180;
        const r1 = 95;
        const r2 = i % 6 === 0 ? 108 : i % 3 === 0 ? 104 : 100;
        ctx.beginPath();
        ctx.moveTo(r1 * Math.cos(a), r1 * Math.sin(a));
        ctx.lineTo(r2 * Math.cos(a), r2 * Math.sin(a));
        ctx.strokeStyle = `${color}${i % 6 === 0 ? '80' : '30'}`;
        ctx.lineWidth = i % 6 === 0 ? 2 : 0.8;
        ctx.stroke();
      }
      ctx.restore();
      
      // Core glow
      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 42);
      coreGrad.addColorStop(0, `${color}${Math.round(pulse * 180).toString(16).padStart(2, '0')}`);
      coreGrad.addColorStop(0.4, `${color}${Math.round(pulse * 80).toString(16).padStart(2, '0')}`);
      coreGrad.addColorStop(0.7, `${color}20`);
      coreGrad.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(cx, cy, 42, 0, Math.PI * 2);
      ctx.fillStyle = coreGrad;
      ctx.fill();
      
      // Core inner circle
      ctx.beginPath();
      ctx.arc(cx, cy, 25 + pulse * 3, 0, Math.PI * 2);
      ctx.fillStyle = `${color}15`;
      ctx.fill();
      ctx.strokeStyle = `${color}50`;
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Center dot
      ctx.beginPath();
      ctx.arc(cx, cy, 4, 0, Math.PI * 2);
      ctx.fillStyle = `${color}cc`;
      ctx.fill();
      
      // Data readout lines (decorative)
      if (isActive) {
        ctx.save();
        ctx.translate(cx, cy);
        for (let i = 0; i < 4; i++) {
          const a = (angle * 3 + i * Math.PI / 2);
          const len = 15 + Math.sin(pulsePhase + i) * 8;
          ctx.beginPath();
          ctx.moveTo(60 * Math.cos(a), 60 * Math.sin(a));
          ctx.lineTo((60 + len) * Math.cos(a), (60 + len) * Math.sin(a));
          ctx.strokeStyle = `${color}60`;
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
        ctx.restore();
      }
      
      animRef.current = requestAnimationFrame(draw);
    }
    
    draw();
    
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  const stateColor = STATE_COLORS[state] || STATE_COLORS.ARMED;

  return (
    <div className="arc-reactor-container" data-testid="arc-reactor" style={{ width: '220px', height: '220px' }}>
      <canvas
        ref={canvasRef}
        style={{ width: '220px', height: '220px' }}
      />
      <div 
        className="reactor-state-label" 
        data-testid="arc-reactor-state-label" 
        style={{ 
          color: stateColor,
          bottom: '-12px',
          textShadow: `0 0 12px ${stateColor}80`
        }}
      >
        {state}
      </div>
    </div>
  );
}
